import React, { ReactNode, useCallback, useMemo, useState } from 'react';

import { format, getZonedDate } from '@eika-infrastruktur/date';
import { capitalizeName, formatAccountNumber } from '@eika-infrastruktur/format';
import { AttentionMessage } from '@eika/attention-message';
import { Button, ButtonTypes, LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { IconTypes } from '@eika/icon';

import FuturePaymentButtonWrapper from '../../components/FuturePayment/FuturePaymentButtonWrapper';
import composeApprovedByString from '../../components/FuturePayment/utils/composeApprovedByString';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { StopPaymentButton } from '../../components/shared/StopPaymentButton';
import { currencyFormatter } from '../../components/shared/numberFormat';
import TransactionActorAddressField from '../../components/transaction/TransactionDetails/TransactionActorAddressField';
import TransactionAmount from '../../components/transaction/TransactionDetails/TransactionAmount';
import TransactionLine from '../../components/transaction/TransactionDetails/TransactionLine';
import { useConfig } from '../../defaultConfig';
import { useAccountNumber } from '../../fetchHooks/useAccount';
import useAccountChange from '../../fetchHooks/useAccountChange';
import { usePaymentItemDetails, useRestartStoppedPayment } from '../../fetchHooks/usePaymentItems';
import type { PaymentItem } from '../../generated-types';
import { useActorReceiver } from '../../hooks/useSetReceiver';
import useTranslation from '../../hooks/useTranslation';
import { useSigningContext } from '../../signingContext/signingContext';
import { Actions } from '../../signingContext/signingTypes';
import StoppedPaymentSigningWrapper from './StoppedPaymentSigningWrapper';
import getDetailedStoppedDescriptionAndLink from './getDetailedStoppedDescription';
import useGuessAvtalegiroFromStoppedPayment from './useGuessAvtalegiroFromStoppedPayment';

import './StoppedPaymentDetailsView.scss';

type Props = {
    paymentItem: PaymentItem | undefined;
    openEditModalState: [boolean, React.Dispatch<boolean>];
    onClose: () => void;
    setIsSuccess: (value: boolean) => void;
};

function StoppedPaymentDetailsView({
    paymentItem,
    openEditModalState: [openEditModal, setOpenEditModal],
    onClose,
    setIsSuccess,
}: Props) {
    const { featureToggles, paymentLimitsLink, accountLink, avtalegiroLink } = useConfig();
    const [expandedDeletePaymentDialogue, setExpandedDeletePaymentDialogue] = useState(false);

    const toAccount = useActorReceiver(paymentItem?.receiver, paymentItem?.paymentItemType === 'FOREIGN_PAYMENT');
    const { canEdit = false, canRestart = false, canReject = false } = paymentItem?.permissions ?? {};

    const translated = useTranslation();
    const refreshPaymentItems = useAccountChange();
    const { signingCompleted, dispatch } = useSigningContext();

    const { data: paymentDetails, isLoading: isLoadingPaymentDetails } = usePaymentItemDetails(
        paymentItem?.paymentItemId,
        { enabled: paymentItem?.permissions?.canView },
    );

    const { data: fromAccount } = useAccountNumber(
        paymentItem?.from?.accountNumber ?? paymentDetails?.from.accountNumber,
    );

    const { optimisticAvtalegiro, invalidateQuery, status } = useGuessAvtalegiroFromStoppedPayment(
        paymentItem,
        toAccount,
    );

    const onClosePaymentItem = useCallback(() => {
        if (signingCompleted) {
            refreshPaymentItems();
        }
        invalidateQuery();
        dispatch({ type: Actions.RESET });
        onClose();
    }, [dispatch, onClose, refreshPaymentItems, signingCompleted, invalidateQuery]);

    const approvers = useMemo(() => {
        const currentApprovers = paymentDetails?.approvers ?? [];

        if (currentApprovers.length === 0) return undefined;
        const restOfApprovers = currentApprovers.filter((x) => !x.firstApprover);
        const firstApprover = currentApprovers.find((x) => x.firstApprover);
        return { firstApprover, restOfApprovers };
    }, [paymentDetails]);

    const restartPayment = useRestartStoppedPayment();

    const detailedStoppedDescription = useMemo((): { text: string; link: ReactNode | undefined } | null => {
        const links = { paymentLimitsLink, accountLink, avtalegiroLink };
        return getDetailedStoppedDescriptionAndLink(translated, links, fromAccount, paymentItem, status !== 'success');
    }, [paymentItem, paymentLimitsLink, accountLink, fromAccount, translated, avtalegiroLink, status]);

    const onRestartPayment = useCallback(() => {
        if (!paymentItem) return;
        restartPayment.mutateAsync(paymentItem.paymentItemId).then(() => setIsSuccess(true));
    }, [restartPayment, paymentItem, setIsSuccess]);

    if (!paymentItem) return null;

    if (restartPayment.error) {
        return (
            <ErrorCardWrapper exception={restartPayment.error}>
                <div className="stopped-payment__details-error-wrapper">
                    <LinkButton onClick={() => restartPayment.reset()}>{translated.close}</LinkButton>
                    <ButtonWithSpinner
                        buttonType={ButtonTypes.STANDARD_POSITIVE}
                        showSpinner={restartPayment.isPending}
                        onClick={() => restartPayment.mutate(paymentItem.paymentItemId)}
                    >
                        {translated.tryAgain}
                    </ButtonWithSpinner>
                </div>
            </ErrorCardWrapper>
        );
    }
    return (
        <StoppedPaymentSigningWrapper
            onClose={onClosePaymentItem}
            paymentType={paymentItem.paymentItemType}
            paymentDate={getZonedDate(paymentItem.dueDate)}
            active={!openEditModal}
        >
            <div className="stopped-payment__details-wrapper">
                <TransactionAmount
                    value={paymentItem.estimatedAmount ?? paymentItem.amount}
                    typeLabel={
                        paymentItem.paymentItemType !== 'UNKNOWN' && paymentItem.paymentItemType
                            ? translated.futurePaymentType[paymentItem.paymentItemType]
                            : undefined
                    }
                    eFakturaId={paymentDetails?.eFakturaId}
                    eFakturaLoading={paymentItem.paymentItemType === 'EFAKTURA' ? isLoadingPaymentDetails : false}
                    additionalInfo={
                        paymentItem.paymentItemType === 'FOREIGN_PAYMENT'
                            ? translated.futurePaymentDetails.calculatedAmount
                            : undefined
                    }
                    eFakturaLinkClassName="stopped-payment__details-eFaktura-link"
                />
                {detailedStoppedDescription?.text && (
                    <AttentionMessage
                        type="attention"
                        className="stopped-payment__details-error-message"
                        icon={IconTypes.UTROPSTEGN_24}
                        clickableItem={detailedStoppedDescription?.link}
                        text={detailedStoppedDescription?.text}
                    />
                )}
                <div className="stopped-payment__details">
                    <TransactionLine
                        label={translated.from}
                        value={fromAccount?.alias ?? paymentDetails?.from.name ?? paymentItem.from?.name}
                        customValue={
                            paymentItem?.from?.accountNumber && (
                                <span className="transaction-details__payer-account-number">
                                    {formatAccountNumber(paymentItem.from.accountNumber)}
                                </span>
                            )
                        }
                    />
                    <TransactionLine
                        label={translated.to}
                        value={toAccount?.accountName}
                        customValue={
                            <span className="transaction-details__payer-account-number">
                                {toAccount?.accountNumber && formatAccountNumber(toAccount.accountNumber)}
                            </span>
                        }
                        isLoading={!toAccount}
                    />
                    <TransactionLine
                        value={paymentDetails?.kid?.value ?? optimisticAvtalegiro?.kid?.value}
                        label={translated.transactionDetails.kid}
                    />
                    <TransactionLine
                        value={paymentDetails?.dueDate}
                        label={translated.date}
                        formatter={(date: string) => format(getZonedDate(date), 'dd.MM.yyyy')}
                    />
                    <TransactionLine
                        show={paymentItem?.paymentItemType === 'FOREIGN_PAYMENT'}
                        label={translated.futurePaymentDetails.amountInCurrency}
                        value={paymentItem.amount}
                        formatter={(value) => currencyFormatter(value, paymentItem.currency)}
                    />
                    <TransactionLine
                        value={paymentDetails?.note ?? paymentItem.note}
                        label={translated.transactionDetails.message}
                    />
                    {paymentDetails?.note !== paymentDetails?.ownText && (
                        <TransactionLine
                            value={paymentDetails?.ownText}
                            label={translated.transactionDetails.ownMessage}
                            isLoading={isLoadingPaymentDetails}
                        />
                    )}

                    <TransactionActorAddressField
                        label={translated.transactionDetails.receiver.address}
                        address={paymentDetails?.receiver?.address}
                        isLoading={isLoadingPaymentDetails}
                    />
                    <TransactionLine
                        value={capitalizeName(approvers?.firstApprover?.name ?? paymentItem?.initiator?.name)}
                        label={translated.futurePaymentDetails.paidBy}
                    />
                    {approvers?.restOfApprovers && (
                        <TransactionLine
                            label={translated.transactionDetails.approvers.approvedBy}
                            value={composeApprovedByString(approvers.restOfApprovers)}
                        />
                    )}
                </div>
            </div>
            <FuturePaymentButtonWrapper>
                <FuturePaymentButtonWrapper.FirstRow>
                    {canEdit && !expandedDeletePaymentDialogue && (
                        <Button
                            buttonType="negative"
                            onClick={() => setOpenEditModal(true)}
                            data-testid="change-future-payment-button"
                        >
                            {translated.futurePaymentDetails.change}
                        </Button>
                    )}
                    {canRestart && featureToggles.enablePayNow && !expandedDeletePaymentDialogue && (
                        <ButtonWithSpinner
                            buttonType="positive"
                            showSpinner={restartPayment.isPending}
                            showSuccess={restartPayment.isSuccess}
                            onClick={onRestartPayment}
                            data-testid="restart-payment-item"
                        >
                            {translated.tryAgain}
                        </ButtonWithSpinner>
                    )}
                    {canReject && !canEdit && paymentItem && (
                        <StopPaymentButton
                            paymentId={paymentItem.paymentItemId.value}
                            paymentType={paymentItem.paymentItemType}
                            onExpandDialog={setExpandedDeletePaymentDialogue}
                        />
                    )}
                </FuturePaymentButtonWrapper.FirstRow>
            </FuturePaymentButtonWrapper>
        </StoppedPaymentSigningWrapper>
    );
}

export default StoppedPaymentDetailsView;
